/**
 * 全应用**唯一**的牌面渲染组件（§13 文件树的硬性约定）。
 *
 * 两条安全不变式，写在类型之外靠这里兜住：
 *  1. `card === null` ⟹ 正面**不进 DOM**。不是 `opacity:0`、不是 `visibility:hidden`——
 *     截图、读屏、DevTools 都能穿透那两种做法（红线 #12 第 6 条）。
 *     hero 的「按住看牌」与遮牌页因此都只要把 `card` 传 null 就绝对安全。
 *  2. `card === null` ⟹ `faceUp` 强制为 false（§11.3 对 CardVM 的断言）。
 *     调用方传了非法组合也不会露牌，只会看到背面。
 *
 * 牌面零图片请求（§11.6 第 7 条）：点数是文本，花色是 Unicode 符号，牌背纹样与卡纸质感
 * 全部由 CSS 渐变生成，不发任何网络请求。
 *
 * 版式按真实扑克：**左上角点数+花色**（竖排角标）+ **中心大花色**。
 * 座位上的迷你牌只有 17..24px 宽，角标缩到 5px 谁都读不出来，
 * 那一档由 CSS（`.card--seat`）改成「点数+花色居中」——组件这里不分支。
 */

import { memo } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { CardId } from '@poker/shared'

import { fxKey } from '../fx/FxController.js'
import { useFxNode } from '../fx/FxLayer.js'

import { cardCode, cardLabel, hiddenCardLabel, rankText, suitKey, suitSymbol } from './cardFormat.js'
import './table.css'

export type CardSize = 'board' | 'hero' | 'seat'

export interface CardProps {
  /** null = 这张牌对当前视角不可见（或还没发出来）。正面不会出现在 DOM 里。 */
  readonly card: CardId | null
  readonly faceUp: boolean
  readonly size: CardSize
  /** 动效层注册名（fxKey.board(i) / fxKey.hero(i) / fxKey.hole(seat, i)）。省略则不参与动画。 */
  readonly fxName?: string
  /** 不在最佳五张里：灰化 + 降透明（class 一次切换，不插值 filter） */
  readonly dim?: boolean
  /** 在最佳五张里：描一圈金边 */
  readonly best?: boolean
  /** 公共牌宽度是动态的（fitBoardCardWidth），只有它需要内联尺寸 */
  readonly width?: number
  readonly height?: number
  readonly className?: string
}

function CardImpl({
  card,
  faceUp,
  size,
  fxName,
  dim = false,
  best = false,
  width,
  height,
  className,
}: CardProps): ReactNode {
  // ★ 不变式 2：没有牌就一定是背面
  const up = faceUp && card !== null
  const rootRef = useFxNode(fxName ?? '')
  const innerRef = useFxNode(fxName === undefined ? '' : fxKey.inner(fxName))

  const classes = ['card', `card--${size}`]
  if (dim) classes.push('card--dim')
  if (best) classes.push('card--best')
  if (className !== undefined) classes.push(className)

  const style: CSSProperties = {}
  if (width !== undefined) {
    style.width = `${width}px`
    // font-size 跟着宽度走，卡面上的点数/花色全用 em，缩放时比例恒定
    style.fontSize = `${width}px`
  }
  if (height !== undefined) style.height = `${height}px`

  return (
    <div
      className={classes.join(' ')}
      style={style}
      data-face={up ? 'up' : 'down'}
      data-card={card === null ? undefined : cardCode(card)}
      role="img"
      aria-label={card !== null && up ? cardLabel(card) : hiddenCardLabel()}
      ref={fxName === undefined ? undefined : rootRef}
    >
      <div className="card__inner" ref={fxName === undefined ? undefined : innerRef}>
        <div className="card__face card__back" />
        {/* ★ 不变式 1：card 为 null 时正面整块不存在 */}
        {card !== null && (
          <div className={`card__face card__front card__front--${suitKey(card)}`}>
            <span className="card__corner">
              {/* rankText 保证 10 显示成 "10" 而不是 "T"（cardFormat.ts 的首要职责） */}
              <span className="card__rank">{rankText(card)}</span>
              <span className="card__pip">{suitSymbol(card)}</span>
            </span>
            {/* 中心大花色纯装饰：花色已经在角标里读过一遍，读屏再念一次是噪音 */}
            <span className="card__center" aria-hidden="true">
              {suitSymbol(card)}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * memo 是必需品不是优化：一手牌里牌桌会重渲染几十次，
 * 而 52 张牌的牌面在一手之内基本不变（§11.6 第 8 条「React 渲染隔离」）。
 */
export const Card = memo(CardImpl)

/** 还没发出来的公共牌位 */
export function CardSlot({ width, height }: { width: number; height: number }): ReactNode {
  return (
    <div
      className="card-slot"
      style={{ width: `${width}px`, height: `${height}px` }}
      aria-hidden="true"
    />
  )
}
